import React from 'react';
import { PageHero } from '../../components/ui/PageHero';
import { QuoteBanner } from '../../components/ui/QuoteBanner';
import { ReviewNote } from '../../components/ui/ReviewNote';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { ButtonLink } from '../../components/ui/ButtonLink';
import { useSeo } from '../../hooks/useSeo';
import { pageMeta } from '../../data/seo';
import { managingDirector, teamMembers } from '../../data/team';
import { company } from '../../data/site';
import { images } from '../../data/images';
export function ManagementTeam() {
  useSeo(pageMeta.team);
  return <>
      <PageHero title="Management & technical team" intro="Civil, structural, environmental and water engineering qualifications across management, project and site roles." image={images.surveyor} breadcrumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'About Us'
    }, {
      label: 'Management & Technical Team'
    }]} data-id="element-564" data-mp="3qop9xcefi6jn" />

      <section aria-labelledby="md-title" className="bg-charcoal py-20 md:py-24" data-id="element-565" data-mp="3qnl4ezbnfo5u">
        <div className="mx-auto grid max-w-site gap-10 px-5 md:px-8 lg:grid-cols-[320px_1fr] lg:items-center lg:gap-16" data-id="element-566" data-mp="3qlh1ajlkqt6m">
          <div className="flex aspect-square w-full max-w-[320px] items-center justify-center bg-brand-action" aria-hidden="true" data-id="element-567" data-mp="480bupad36df">
            <span className="font-display text-[120px] font-extrabold leading-none text-white" data-id="element-568" data-mp="95iejxxscpcz">OR</span>
          </div>
          <div data-id="element-569" data-mp="37qa5xgxucyrg">
            <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-brand-light" data-id="element-570" data-mp="37qjv52b2b1w3">{managingDirector.position}</p>
            <h2 id="md-title" className="mt-3 font-display text-[48px] font-extrabold uppercase leading-[0.92] text-white md:text-[64px]" data-id="element-571" data-mp="3hm3rfptjg2ld">
              {managingDirector.name}
            </h2>
            <dl className="mt-8 grid gap-6 border-t border-white/15 pt-6 sm:grid-cols-3" data-id="element-572" data-mp="wwxjzzkuzmsg">
              <div data-id="element-573" data-mp="11v8w6gymm9du">
                <dt className="text-[12px] font-semibold uppercase tracking-[0.14em] text-steel-400" data-id="element-574" data-mp="11upn7pqyevby">Qualification</dt>
                <dd className="mt-1 text-[16px] text-white" data-id="element-575" data-mp="2xxc6pbi1h8y8">{managingDirector.qualification}</dd>
              </div>
              <div data-id="element-576" data-mp="2szar50jtnrri">
                <dt className="text-[12px] font-semibold uppercase tracking-[0.14em] text-steel-400" data-id="element-577" data-mp="2t1wvkrbf1r5i">Experience</dt>
                <dd className="mt-1 text-[16px] text-white" data-id="element-578" data-mp="32xzyw0i07vu1">{managingDirector.experience}</dd>
              </div>
              <div data-id="element-579" data-mp="2f7mgl1p81bcn">
                <dt className="text-[12px] font-semibold uppercase tracking-[0.14em] text-steel-400" data-id="element-580" data-mp="2f977mhcbuc5r">Role in company</dt>
                <dd className="mt-1 text-[16px] text-white" data-id="element-581" data-mp="2aaw0wfhbwhrr">Director and shareholder</dd>
              </div>
            </dl>
            <ReviewNote className="mt-6" data-id="element-582" data-mp="2o2am8sypkv1z">
              Confirm Obokeng Rabai is still Managing Director. No staff photographs were supplied, so initials are used.
              Add only approved portraits.
            </ReviewNote>
          </div>
        </div>
      </section>

      <section aria-labelledby="team-title" className="bg-charcoal-950 py-20 md:py-24" data-id="element-583" data-mp="1ribbj80u8usp">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-584" data-mp="1hd1yo5e7c2jy">
          <SectionHeading id="team-title" label="Head office & site" title="Management and technical staff" intro={`The management and head office team as listed in the company profile, supporting ${company.permanentEmployees} permanent employees and ${company.casualEmployees} casual employees on contract.`} className="max-w-3xl" data-id="element-585" data-mp="1m5v3et9o7aw1" />

          <div className="mt-10 hidden md:block" data-id="element-586" data-mp="254qsxral313j">
            <table className="w-full text-left" data-id="element-587" data-mp="2524pa2qtd5ql">
              <thead data-id="element-588" data-mp="1q7q1zq07va64">
                <tr className="border-b-2 border-brand-red text-[12px] uppercase tracking-[0.14em] text-steel-400" data-id="element-589" data-mp="1q658m3pn1x9u">
                  <th scope="col" className="py-3 pr-6 font-semibold" data-id="element-590" data-mp="1v46qwmf8psk9">Name</th>
                  <th scope="col" className="py-3 pr-6 font-semibold" data-id="element-591" data-mp="er03kt21bmn2">Position</th>
                  <th scope="col" className="py-3 pr-6 font-semibold" data-id="element-592" data-mp="jr4johz16fcr">Qualification</th>
                  <th scope="col" className="py-3 font-semibold" data-id="element-593" data-mp="zyixpq9n0v1p">Experience</th>
                </tr>
              </thead>
              <tbody data-id="element-594" data-mp="v696m6wsdoxu">
                {teamMembers.map(member => <tr key={member.name} className="border-b border-white/10 align-top" data-id="element-595" data-mp="q7o7ybx6ndbi">
                    <th scope="row" className="py-4 pr-6 font-display text-[19px] font-bold uppercase text-white" data-id="element-596" data-mp="l9cy416w12ta">
                      {member.name}
                    </th>
                    <td className="py-4 pr-6 text-[15px] text-steel-100" data-id="element-597" data-mp="1iusewmfu61px">{member.position}</td>
                    <td className="py-4 pr-6 text-[15px] text-steel-300" data-id="element-598" data-mp="1dvp9v1zuvvhy">{member.qualification}</td>
                    <td className="py-4 text-[15px] text-steel-100" data-id="element-599" data-mp="190s361q1ty88">{member.experience}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <ul className="mt-10 divide-y divide-white/10 border-y border-white/10 md:hidden" data-id="element-600" data-mp="3tq7sstdj7gwa">
            {teamMembers.map(member => <li key={member.name} className="py-4" data-id="element-601" data-mp="2482jizg355al">
                <p className="font-display text-[20px] font-bold uppercase text-white" data-id="element-602" data-mp="249lz0ntx3cbb">{member.name}</p>
                <p className="text-[15px] text-brand-light" data-id="element-603" data-mp="2975axh357d03">{member.position}</p>
                <p className="mt-1 text-sm text-steel-300" data-id="element-604" data-mp="2e56wc8o1joeq">
                  {member.qualification}
                  {member.experience && ` · ${member.experience}`}
                </p>
              </li>)}
          </ul>

          <ReviewNote className="mt-8" data-id="element-605" data-mp="2ny4gej8ollfg">
            Names, qualifications and experience come from the profile staff table. Confirm each person is still with the
            company and has agreed to appear on the website.
          </ReviewNote>

          <ButtonLink to="/about/quality-project-management" variant="outline" showArrow className="mt-10" data-id="element-606" data-mp="31uhz1voybsvi">
            How Projects Are Managed
          </ButtonLink>
        </div>
      </section>

      <QuoteBanner data-id="element-607" data-mp="17gqkchrrmit7" />
    </>;
}